import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from '../ThemeContext';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { Lock, Search, Filter, TrendingUp, TrendingDown } from 'lucide-react';
import StockService from '../../Api_Module/Api_Services/StockService';
import { useParams, useNavigate } from 'react-router-dom';
import { formatNumber, formatCurrency } from '../../utils/Numberformat';

// --- Interfaces for Real Data Integration ---
export interface InstitutionalTrendData {
    period: string; // e.g., "2024 Q3"
    date?: string;
    investorsHolding?: number;
    numberOf13Fshares?: number;
    newPositions: number;
    increased: number;
    maintained: number;
    reduced: number;
    closed: number;
}

export interface TopMoverData {
    id: string;
    name: string;
    logo?: string; // URL to logo
    location: string;
    change: number; // Percentage change
    shares: number;
    amountSpent: number;
    avgPricePaid: number;
    quarterEndPrice?: number;
    isLocked?: boolean;
}

export interface HoldingData {
    id: string;
    name: string;
    holdingDate: string;
    value: number;
    shares: number;
    changeShares: number;
    changePercent: number;
    avgPricePaid?: number;
    isLocked?: boolean;
}

const InstitutionalOwnership: React.FC = () => {
    const { theme } = useTheme();
    const { symbol } = useParams<{ symbol: string }>();
    const navigate = useNavigate();
    const { marketData, marketCurrentPrice } = useSelector((state: any) => state.app || {});
    const [selectedQuarter, setSelectedQuarter] = useState('2025 Q3');
    const [currentPage, setCurrentPage] = useState(0);
    const itemsPerPage = 20; // Increased for better infinite scroll experience
    const [chartData, setChartData] = useState<InstitutionalTrendData[]>([]);
    const [topBuyers, setTopBuyers] = useState<TopMoverData[]>([]);
    const [topSellers, setTopSellers] = useState<TopMoverData[]>([]);
    const [holdings, setHoldings] = useState<HoldingData[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [totalHoldings, setTotalHoldings] = useState(0);
    const [sharePriceData, setSharePriceData] = useState<{ date: string; close: number }[]>([]);

    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const observer = useRef<IntersectionObserver | null>(null);

    const lastHoldingElementRef = useCallback((node: HTMLTableRowElement) => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setCurrentPage(prev => prev + 1);
            }
        });
        if (node) observer.current.observe(node);
    }, [loading, hasMore]);

    // Fetch institutional ownership history when symbol changes
    useEffect(() => {
        if (!symbol) return;

        StockService.getInstitutionalOwnershipHistory(symbol)
            .then((res) => {
                const quarters = res?.quarters ?? [];
                const transformed: InstitutionalTrendData[] = quarters.map((q: any) => ({
                    period: q.period,
                    date: q.date,
                    investorsHolding: q.investorsHolding,
                    numberOf13Fshares: q.numberOf13Fshares,
                    newPositions: q.newPositions,
                    increased: q.increasedPositions,
                    maintained: q.maintainedPositions,
                    reduced: q.reducedPositions,
                    closed: q.closedPositions,
                }));
                setChartData(transformed);
                // Set default selected quarter to the most recent one if available
                if (transformed.length > 0) {
                    setSelectedQuarter(transformed[transformed.length - 1].period);
                }
            })
            .catch((err) => console.error('Failed to fetch institutional ownership history', err));
    }, [symbol]);

    // Fetch share price series data
    useEffect(() => {
        if (!symbol) return;

        StockService.getSharePriceSeries(symbol)
            .then((res: any) => {
                const data = Array.isArray(res) ? res : (res?.data || []);
                setSharePriceData(data);
            })
            .catch((err: any) => console.error('Failed to fetch share price series', err));
    }, [symbol]);

    // Fetch data that depends on selectedQuarter
    useEffect(() => {
        if (!symbol) return;

        // Top Buyers
        StockService.getInstitutionalTopBuyers(symbol, selectedQuarter)
            .then((res) => {
                const data = Array.isArray(res) ? res : (res?.data || []);
                const transformed = data.map((item: any, index: number) => ({
                    id: item.cik || item.id || `buyer-${index}`,
                    name: item.investorName || item.name || 'Unknown Fund',
                    location: item.location || 'N/A',
                    change: item.changeInSharesNumberPercentage || item.changePercent || 0,
                    shares: item.changeInSharesNumber || item.shares || 0,
                    amountSpent: item.amountSpent || 0,
                    avgPricePaid: item.avgPricePaid || 0,
                    quarterEndPrice: item.quarterEndPrice || 0,
                    isLocked: item.isLocked || false,
                }));
                setTopBuyers(transformed);
            })
            .catch((err) => console.error('Failed to fetch top buyers', err));

        // Top Sellers
        StockService.getInstitutionalTopSellers(symbol, selectedQuarter)
            .then((res) => {
                const data = Array.isArray(res) ? res : (res?.data || []);
                const transformed = data.map((item: any, index: number) => ({
                    id: item.cik || item.id || `seller-${index}`,
                    name: item.investorName || item.name || 'Unknown Fund',
                    location: item.location || 'N/A',
                    change: item.changeInSharesNumberPercentage || item.changePercent || 0,
                    shares: item.changeInSharesNumber || item.shares || 0,
                    amountSpent: item.amountSpent || 0,
                    avgPricePaid: item.avgPricePaid || 0,
                    quarterEndPrice: item.quarterEndPrice || 0,
                    isLocked: item.isLocked || false,
                }));
                setTopSellers(transformed);
            })
            .catch((err) => console.error('Failed to fetch top sellers', err));

        // Reset holdings when quarter changes
        setHoldings([]);
        setCurrentPage(0);
        setHasMore(true);

    }, [symbol, selectedQuarter]);

    // Fetch holdings when pagination, search, or quarter changes
    useEffect(() => {
        if (!symbol) return;
        setLoading(true);

        StockService.getInstitutionalHoldings(symbol, selectedQuarter, currentPage, itemsPerPage, searchQuery)
            .then((res) => {
                // Handle response based on search mode vs lazy load mode
                const data = Array.isArray(res?.holdings) ? res.holdings : (Array.isArray(res?.content) ? res.content : (Array.isArray(res) ? res : []));

                // Update hasMore based on API response or check if we got full page
                const moreAvailable = res?.hasMore ?? (data.length >= itemsPerPage);
                setHasMore(moreAvailable);

                // Update total count if available (mostly for search)
                if (res?.total !== undefined && res?.total !== null) {
                    setTotalHoldings(res.total);
                }

                const transformed = data.map((item: any, index: number) => ({
                    id: item.cik || item.id || `holding-${currentPage}-${index}`,
                    name: item.investorName || item.name || 'Unknown Fund',
                    holdingDate: item.date || item.reportDate || 'N/A',
                    value: item.marketValue || item.value || 0,
                    shares: item.sharesNumber || item.shares || 0,
                    changeShares: item.changeInSharesNumber || item.changeShares || 0,
                    changePercent: item.changeInSharesNumberPercentage || item.changePercent || 0,
                    avgPricePaid: item.avgPricePaid || 0,
                    isLocked: item.isLocked || false,
                }));

                if (currentPage === 0) {
                    setHoldings(transformed);
                } else {
                    setHoldings(prev => [...prev, ...transformed]);
                }
            })
            .catch((err) => console.error('Failed to fetch holdings', err))
            .finally(() => setLoading(false));
    }, [symbol, selectedQuarter, currentPage, itemsPerPage, searchQuery]);


    // Theme colors
    const textColor = theme === 'dark' ? '#e5e7eb' : '#374151';
    const gridColor = theme === 'dark' ? '#374151' : '#e5e7eb';
    const cardBg = theme === 'dark' ? 'bg-gray-800' : 'bg-white';
    const borderColor = theme === 'dark' ? 'border-gray-700' : 'border-gray-200';

    const parsePeriod = (period: string): { year: number; quarter: number } | null => {
        const parts = period.split('Q');
        if (parts.length !== 2) return null;
        const year = parseInt(parts[0].trim(), 10);
        const quarter = parseInt(parts[1].trim(), 10);
        if (!Number.isFinite(year) || !Number.isFinite(quarter)) return null;
        if (quarter < 1 || quarter > 4) return null;
        return { year, quarter };
    };

    const formatPeriod = (year: number, quarter: number) => `${year} Q${quarter}`;
    const getQuarterStart = (year: number, quarter: number) => new Date(year, (quarter - 1) * 3, 1);
    const getQuarterEnd = (year: number, quarter: number) => new Date(year, quarter * 3, 0);

    const formatQuarterEndLabel = (period: string): string => {
        const parsed = parsePeriod(period);
        if (!parsed) return period;
        const end = getQuarterEnd(parsed.year, parsed.quarter);
        if (Number.isNaN(end.getTime())) return period;
        const month = end.toLocaleString(undefined, { month: 'short' });
        const yy = String(parsed.year).slice(-2);
        return `${month}-${yy}`;
    };

    const sortedChartData = [...chartData].sort((a, b) => {
        const pa = parsePeriod(a.period);
        const pb = parsePeriod(b.period);
        if (!pa && !pb) return 0;
        if (!pa) return -1;
        if (!pb) return 1;
        return pa.year !== pb.year ? pa.year - pb.year : pa.quarter - pb.quarter;
    });

    const sharePriceDataSorted = [...sharePriceData].sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    const chartDataByPeriod = new Map(sortedChartData.map(d => [d.period, d] as const));
    const basePeriods = sortedChartData.map(d => d.period);

    const liveQuotePrice: number | null = (() => {
        const p = marketData?.price ?? marketCurrentPrice;
        return typeof p === 'number' && Number.isFinite(p) ? p : null;
    })();

    const liveQuoteDate = liveQuotePrice !== null ? new Date() : null;

    const lastPriceDate = sharePriceDataSorted.length > 0
        ? new Date(sharePriceDataSorted[sharePriceDataSorted.length - 1].date)
        : null;

    const xCategories: string[] = (() => {
        if (basePeriods.length === 0) return [];
        const lastSeriesDateValid = lastPriceDate && !Number.isNaN(lastPriceDate.getTime()) ? lastPriceDate : null;
        const liveDateValid = liveQuoteDate && !Number.isNaN(liveQuoteDate.getTime()) ? liveQuoteDate : null;
        const endDate = (() => {
            if (lastSeriesDateValid && liveDateValid) {
                return lastSeriesDateValid.getTime() >= liveDateValid.getTime() ? lastSeriesDateValid : liveDateValid;
            }
            return lastSeriesDateValid ?? liveDateValid;
        })();
        if (!endDate) return basePeriods;
        const first = parsePeriod(basePeriods[0]);
        if (!first) return basePeriods;
        const endYear = endDate.getFullYear();
        const endQuarter = Math.floor(endDate.getMonth() / 3) + 1;
        const out: string[] = [];
        let y = first.year;
        let q = first.quarter;
        while (y < endYear || (y === endYear && q <= endQuarter)) {
            out.push(formatPeriod(y, q));
            q += 1;
            if (q > 4) {
                q = 1;
                y += 1;
            }
            if (out.length > 200) break;
        }
        return out.length > 0 ? out : basePeriods;
    })();

    const newPositionsSeries: (number | null)[] = xCategories.map(p => chartDataByPeriod.get(p)?.newPositions ?? null);
    const increasedSeries: (number | null)[] = xCategories.map(p => chartDataByPeriod.get(p)?.increased ?? null);
    const maintainedSeries: (number | null)[] = xCategories.map(p => chartDataByPeriod.get(p)?.maintained ?? null);
    const reducedSeries: (number | null)[] = xCategories.map(p => chartDataByPeriod.get(p)?.reduced ?? null);
    const closedSeries: (number | null)[] = xCategories.map(p => chartDataByPeriod.get(p)?.closed ?? null);

    const ownershipSharesSeries: (number | null)[] = xCategories.map(p => {
        const v = chartDataByPeriod.get(p)?.numberOf13Fshares;
        return typeof v === 'number' && Number.isFinite(v) ? v : null;
    });

    const fintelTickPositions: number[] | undefined = (() => {
        if (xCategories.length === 0) return undefined;
        const out: number[] = [];
        for (let i = 0; i < xCategories.length; i += 1) {
            const parsed = parsePeriod(xCategories[i]);
            if (!parsed) continue;
            if (parsed.quarter === 2 || parsed.quarter === 4) out.push(i);
        }
        if (out.length === 0) return xCategories.map((_, i) => i);
        if (out[0] !== 0) out.unshift(0);
        const lastIdx = xCategories.length - 1;
        if (out[out.length - 1] !== lastIdx) out.push(lastIdx);
        return out;
    })();

    const latestOwnershipInfo: { period: string; date: Date } | null = (() => {
        for (let i = xCategories.length - 1; i >= 0; i -= 1) {
            const period = xCategories[i];
            const row = chartDataByPeriod.get(period);
            const shares = row?.numberOf13Fshares;
            if (typeof shares !== 'number' || !Number.isFinite(shares)) continue;

            const dtFromApi = row?.date ? new Date(row.date) : null;
            const dtFromApiValid = dtFromApi && !Number.isNaN(dtFromApi.getTime()) ? dtFromApi : null;
            if (dtFromApiValid) return { period, date: dtFromApiValid };

            const parsed = parsePeriod(period);
            if (!parsed) continue;
            const end = getQuarterEnd(parsed.year, parsed.quarter);
            if (!Number.isNaN(end.getTime())) return { period, date: end };
        }
        return null;
    })();

    const periodToIndex = new Map(xCategories.map((p, i) => [p, i] as const));

    // Transform share price data to continuous line across quarterly categories
    // Map each daily price to its corresponding position between quarterly categories
    const sharePriceSeriesData = xCategories.length > 0 ? sharePriceDataSorted.map((price) => {
        const priceDate = new Date(price.date);
        if (Number.isNaN(priceDate.getTime())) return null;

        const year = priceDate.getFullYear();
        const quarter = Math.floor(priceDate.getMonth() / 3) + 1;
        const period = formatPeriod(year, quarter);
        const periodIndex = periodToIndex.get(period);
        if (periodIndex === undefined) return null;

        const quarterStartDate = getQuarterStart(year, quarter);
        const quarterEndDate = getQuarterEnd(year, quarter);
        const denom = quarterEndDate.getTime() - quarterStartDate.getTime();
        const rawFraction = denom > 0 ? (priceDate.getTime() - quarterStartDate.getTime()) / denom : 0;
        const fraction = Math.min(0.999999, Math.max(0, rawFraction));

        return [periodIndex + fraction, price.close];
    }).filter((point): point is [number, number] => point !== null) : [];

    const sharePriceSeriesDataWithLive: [number, number][] = (() => {
        if (sharePriceSeriesData.length === 0) return [];
        if (!liveQuoteDate || liveQuotePrice === null) return sharePriceSeriesData;
        if (xCategories.length === 0) return sharePriceSeriesData;

        const year = liveQuoteDate.getFullYear();
        const quarter = Math.floor(liveQuoteDate.getMonth() / 3) + 1;
        const period = formatPeriod(year, quarter);
        const periodIndex = periodToIndex.get(period);
        if (periodIndex === undefined) return sharePriceSeriesData;

        const quarterStartDate = getQuarterStart(year, quarter);
        const quarterEndDate = getQuarterEnd(year, quarter);
        const denom = quarterEndDate.getTime() - quarterStartDate.getTime();
        const rawFraction = denom > 0 ? (liveQuoteDate.getTime() - quarterStartDate.getTime()) / denom : 0;
        const fraction = Math.min(0.999999, Math.max(0, rawFraction));
        const x = periodIndex + fraction;

        const last = sharePriceDataSorted.length > 0 ? sharePriceDataSorted[sharePriceDataSorted.length - 1] : null;
        const lastClose = last && typeof last.close === 'number' && Number.isFinite(last.close) ? last.close : null;
        const lastDt = last ? new Date(last.date) : null;
        const lastDtValid = lastDt && !Number.isNaN(lastDt.getTime()) ? lastDt : null;

        const msDay = 24 * 60 * 60 * 1000;
        const staleByTime = !lastDtValid || (liveQuoteDate.getTime() - lastDtValid.getTime()) > (3 * msDay);
        const staleByPrice = lastClose === null ? true : (Math.abs(lastClose - liveQuotePrice) / Math.max(1e-9, Math.abs(liveQuotePrice))) > 0.05;

        if (!staleByTime && !staleByPrice) return sharePriceSeriesData;

        const maxX = xCategories.length - 0.001;
        const clampedX = Math.min(maxX, Math.max(0, x));
        return [...sharePriceSeriesData, [clampedX, liveQuotePrice]];
    })();

    // Only show share price if we have meaningful data:
    // - At least 10 data points
    // - Data points span across at least half of the chart's quarters
    const hasValidSharePriceData = sharePriceSeriesDataWithLive.length >= 10 &&
        xCategories.length > 0 &&
        sharePriceSeriesDataWithLive.length > 0 &&
        (Math.max(...sharePriceSeriesDataWithLive.map(p => p[0])) - Math.min(...sharePriceSeriesDataWithLive.map(p => p[0]))) >= (xCategories.length * 0.5);

    const chartOptions: Highcharts.Options = {
        chart: {
            type: 'column',
            backgroundColor: 'transparent',
            height: 350,
            style: { fontFamily: 'Inter, sans-serif' }
        },
        title: { text: undefined },
        xAxis: {
            categories: xCategories,
            min: 0,
            max: xCategories.length > 0 ? xCategories.length - 0.001 : undefined,
            tickPositions: fintelTickPositions,
            lineColor: gridColor,
            tickColor: gridColor,
            labels: {
                style: { color: textColor },
                formatter: function () {
                    if (typeof this.value === 'string') return formatQuarterEndLabel(this.value);
                    const v = typeof this.value === 'number' ? this.value : Number(this.value);
                    const idx = Number.isFinite(v) ? Math.round(v) : NaN;
                    const period = Number.isFinite(idx) ? xCategories[idx] : undefined;
                    return typeof period === 'string' ? formatQuarterEndLabel(period) : '';
                }
            }
        },
        yAxis: [
            {
                title: { text: '13F Shares', style: { color: textColor } },
                gridLineColor: gridColor,
                labels: {
                    style: { color: textColor },
                    formatter: function () {
                        const v = typeof this.value === 'number' ? this.value : Number(this.value);
                        if (!Number.isFinite(v)) return '';
                        const abs = Math.abs(v);
                        if (abs >= 1e9) return `${(v / 1e9).toFixed(1)}B`;
                        if (abs >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
                        if (abs >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
                        return `${Math.round(v)}`;
                    }
                }
            },
            {
                title: { text: 'Share Price ($)', style: { color: textColor } },
                opposite: true,
                gridLineColor: 'transparent',
                labels: { style: { color: textColor } }
            }
        ],
        legend: {
            itemStyle: { color: textColor },
            itemHoverStyle: { color: theme === 'dark' ? '#fff' : '#000' }
        },
        plotOptions: {
            column: {
                stacking: 'normal',
                borderWidth: 0,
                borderRadius: 2
            },
            line: {
                marker: {
                    enabled: false
                }
            }
        },
        tooltip: {
            shared: true,
            backgroundColor: theme === 'dark' ? '#1f2937' : '#ffffff',
            borderColor: theme === 'dark' ? '#374151' : '#e5e7eb',
            style: { color: textColor },
            formatter: function () {
                const ctx: any = this;
                const xVal = ctx.x;

                let header = '';
                if (typeof xVal === 'number' && Number.isFinite(xVal)) {
                    const idx = Math.floor(xVal);
                    const fracRaw = xVal - idx;
                    const frac = Math.min(0.999999, Math.max(0, fracRaw));
                    const isQuarterPoint = Math.abs(fracRaw) < 1e-9;
                    const period = xCategories[idx];

                    if (typeof period === 'string' && period) {
                        const parsed = parsePeriod(period);
                        if (parsed) {
                            const start = getQuarterStart(parsed.year, parsed.quarter);
                            const end = getQuarterEnd(parsed.year, parsed.quarter);
                            if (isQuarterPoint) {
                                const endLabel = Number.isNaN(end.getTime())
                                    ? ''
                                    : end.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
                                const monthLabel = formatQuarterEndLabel(period);
                                header = endLabel ? `${monthLabel} (${endLabel})` : monthLabel;
                            } else {
                                const ms = start.getTime() + (end.getTime() - start.getTime()) * frac;
                                const d = new Date(ms);
                                const dateLabel = Number.isNaN(d.getTime())
                                    ? ''
                                    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
                                header = dateLabel ? `${period} (${dateLabel})` : period;
                            }
                        } else {
                            header = period;
                        }
                    } else {
                        header = String(xVal);
                    }
                } else if (typeof xVal === 'string') {
                    header = xVal;
                }

                const pts: any[] = Array.isArray(ctx.points) ? ctx.points : (ctx.point ? [ctx.point] : []);
                let out = `<span style=\"font-size: 11px; font-weight: 600;\">${header}</span>`;
                for (const p of pts) {
                    const name = p?.series?.name ?? '';
                    const color = p?.color ?? p?.series?.color ?? '#999';
                    const y = typeof p?.y === 'number' && Number.isFinite(p.y) ? p.y : null;

                    let val = '—';
                    if (y !== null) {
                        if (name === 'Share Price') {
                            val = `${y.toFixed(2)} $`;
                        } else if (name === 'Ownership') {
                            val = `${formatNumber(Math.round(y))}`;
                        } else {
                            val = `${Math.round(y)}`;
                        }
                    }
                    out += `<br/><span style=\"color:${color};\">●</span> ${name}: <b>${val}</b>`;
                }
                return out;
            }
        },
        series: [
            { name: 'Ownership', data: ownershipSharesSeries as any, color: '#166534', type: 'column', yAxis: 0 } as Highcharts.SeriesColumnOptions,
            // Only include share price series if we have valid, well-distributed data
            ...(hasValidSharePriceData ? [{
                name: 'Share Price',
                data: sharePriceSeriesDataWithLive,
                color: '#f59e0b',
                type: 'line' as const,
                yAxis: 1,
                lineWidth: 2,
                marker: {
                    enabled: false,
                    states: {
                        hover: {
                            enabled: true,
                            radius: 4
                        }
                    }
                },
                tooltip: {
                    valueSuffix: ' $',
                    valueDecimals: 2
                },
                zIndex: 10
            } as Highcharts.SeriesLineOptions] : [])
        ],
        credits: {
            enabled: true,
            text: 'Powered by StockFortress',
            href: 'https://stockfortress.com',
            style: {
                color: 'rgba(255, 255, 255, 0.4)',
                fontSize: '12px',
                fontWeight: '400',
                cursor: 'pointer'
            },
            position: {
                align: 'right',
                x: -10,
                y: -5
            }
        }
    };

    return (
        <div className={`p-6 ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>
            {/* Header Section */}
            <div className="mb-8">
                <h1 className="text-2xl font-bold mb-2">Institutional Ownership</h1>
                <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    Track smart money flows and institutional sentiment.
                </p>
            </div>

            {/* Chart Section */}
            <div className={`p-6 rounded-xl border ${borderColor} ${cardBg} mb-8 relative`}>
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-lg font-semibold">Institutional Ownership</h2>
                    {latestOwnershipInfo && (
                        <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                            Latest: {formatQuarterEndLabel(latestOwnershipInfo.period)} ({latestOwnershipInfo.date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' })})
                        </div>
                    )}
                </div>

                <div className="relative">
                    <HighchartsReact highcharts={Highcharts} options={chartOptions} />

                    {/* Optional: Remove this overlay if real data is available, or keep as upsell feature */}
                    <div className="absolute inset-0 bg-white/5 backdrop-blur-[1px] flex items-center justify-center z-10 opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                        <div className="bg-black/80 text-white px-4 py-2 rounded-lg flex items-center gap-2">
                            <Lock className="w-4 h-4" />
                            <span className="text-sm font-medium">Upgrade to see full history</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Quarter Selector */}
            <div className="flex overflow-x-auto scrollbar-hide gap-2 mb-8 pb-2">
                {[...chartData].reverse().map((d) => (
                    <button
                        key={d.period}
                        onClick={() => setSelectedQuarter(d.period)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${selectedQuarter === d.period
                            ? (theme === 'dark'
                                ? 'bg-blue-900/20 text-blue-400 ring-1 ring-blue-800'
                                : 'bg-blue-50 text-blue-600 ring-1 ring-blue-200')
                            : (theme === 'dark'
                                ? 'bg-gray-800 text-gray-400 border border-gray-700 hover:bg-gray-700'
                                : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50')
                            }`}
                    >
                        {d.period}
                    </button>
                ))}
            </div>

            {/* Top Movers Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {/* Top Buyers */}
                <div className={`p-6 rounded-xl border ${borderColor} ${cardBg}`}>
                    <div className="flex items-center gap-2 mb-4">
                        <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                            <TrendingUp className="w-5 h-5 text-green-600 dark:text-green-400" />
                        </div>
                        <h3 className="font-semibold">Top Buyers</h3>
                    </div>
                    <div className="space-y-4">
                        {topBuyers.map((buyer) => (
                            <div
                                key={buyer.id}
                                onClick={() => navigate(`/institution/${buyer.id}`)}
                                className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${theme === 'dark' ? 'bg-gray-700/50 hover:bg-gray-700' : 'bg-gray-50 hover:bg-gray-100'}`}
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-xs font-bold">
                                        {buyer.name ? buyer.name.charAt(0) : '?'}
                                    </div>
                                    <div>
                                        <div className="font-medium text-sm">{buyer.name}</div>
                                        {buyer.location && buyer.location !== 'N/A' && (
                                            <div className="text-xs text-gray-500">{buyer.location}</div>
                                        )}
                                    </div>
                                </div>
                                {buyer.isLocked ? (
                                    <Lock className="w-4 h-4 text-gray-400" />
                                ) : (
                                    <div className="text-right">
                                        <div className="text-green-500 font-bold text-sm">
                                            ${formatNumber(buyer.amountSpent)}
                                        </div>
                                        <div className="text-xs text-gray-500">Avg: {formatCurrency(buyer.avgPricePaid)}</div>
                                        {buyer.quarterEndPrice !== undefined && buyer.quarterEndPrice !== 0 && (
                                            <div className="text-xs text-gray-500">
                                                End: {formatCurrency(buyer.quarterEndPrice)}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Top Sellers */}
                <div className={`p-6 rounded-xl border ${borderColor} ${cardBg}`}>
                    <div className="flex items-center gap-2 mb-4">
                        <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                            <TrendingDown className="w-5 h-5 text-red-600 dark:text-red-400" />
                        </div>
                        <h3 className="font-semibold">Top Sellers</h3>
                    </div>
                    <div className="space-y-4">
                        {topSellers.map((seller) => (
                            <div
                                key={seller.id}
                                onClick={() => navigate(`/institution/${seller.id}`)}
                                className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${theme === 'dark' ? 'bg-gray-700/50 hover:bg-gray-700' : 'bg-gray-50 hover:bg-gray-100'}`}
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-xs font-bold">
                                        {seller.name ? seller.name.charAt(0) : '?'}
                                    </div>
                                    <div>
                                        <div className="font-medium text-sm">{seller.name}</div>
                                        {seller.location && seller.location !== 'N/A' && (
                                            <div className="text-xs text-gray-500">{seller.location}</div>
                                        )}
                                    </div>
                                </div>
                                {seller.isLocked ? (
                                    <Lock className="w-4 h-4 text-gray-400" />
                                ) : (
                                    <div className="text-right">
                                        <div className="text-red-500 font-bold text-sm">
                                            ${formatNumber(Math.abs(seller.amountSpent))}
                                        </div>
                                        <div className="text-xs text-gray-500">Avg: {formatCurrency(seller.avgPricePaid)}</div>
                                        {seller.quarterEndPrice !== undefined && seller.quarterEndPrice !== 0 && (
                                            <div className="text-xs text-gray-500">
                                                End: {formatCurrency(seller.quarterEndPrice)}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Holdings Table */}
            <div className={`rounded-xl border ${borderColor} ${cardBg} overflow-hidden`}>
                <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <h3 className="font-semibold">Institutional Holdings</h3>
                    <div className="flex items-center gap-2">
                        <div className="relative">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search funds..."
                                value={searchQuery}
                                onChange={(e) => {
                                    setSearchQuery(e.target.value);
                                    setCurrentPage(0); // Reset to first page on search
                                    setHoldings([]); // Clear current holdings
                                }}
                                className={`pl-9 pr-4 py-1.5 text-sm rounded-lg border ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                            />
                        </div>
                        <button className={`p-1.5 rounded-lg border ${theme === 'dark' ? 'border-gray-600 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-50'}`}>
                            <Filter className="w-4 h-4 text-gray-500" />
                        </button>
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                        <thead className={`text-xs uppercase ${theme === 'dark' ? 'bg-gray-700/50 text-gray-400' : 'bg-gray-50 text-gray-500'}`}>
                            <tr>
                                <th className="px-6 py-3">Fund Name</th>
                                <th className="px-6 py-3 text-right">Avg Price</th>
                                <th className="px-6 py-3 text-right">Trade Value</th>
                                <th className="px-6 py-3 text-right">Shares</th>
                                <th className="px-6 py-3 text-right">Change</th>
                                <th className="px-6 py-3 text-right">% Change</th>
                            </tr>
                        </thead>
                        <tbody>
                            {holdings.map((holding, index) => {
                                // Add ref to the last element for infinite scroll
                                const isLastElement = index === holdings.length - 1;
                                return (
                                    <tr
                                        key={holding.id}
                                        ref={isLastElement ? lastHoldingElementRef : null}
                                        onClick={() => navigate(`/institution/${holding.id}`)}
                                        className={`border-b cursor-pointer transition-colors ${theme === 'dark' ? 'border-gray-700 hover:bg-gray-700/30' : 'border-gray-100 hover:bg-gray-50'}`}
                                    >
                                        <td className="px-6 py-4 font-medium">{holding.name}</td>
                                        {holding.isLocked ? (
                                            <td colSpan={5} className="px-6 py-4 text-center">
                                                <div className="flex items-center justify-center gap-2 text-gray-400">
                                                    <Lock className="w-3 h-3" />
                                                    <span className="text-xs">Upgrade to view details</span>
                                                </div>
                                            </td>
                                        ) : (
                                            <>
                                                <td className="px-6 py-4 text-right">{formatCurrency(holding.avgPricePaid || 0)}</td>
                                                <td className="px-6 py-4 text-right">${formatNumber(holding.value)}</td>
                                                <td className="px-6 py-4 text-right">{formatNumber(holding.shares)}</td>
                                                <td className={`px-6 py-4 text-right ${holding.changeShares >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                                    {holding.changeShares > 0 ? '+' : ''}{formatNumber(holding.changeShares)}
                                                </td>
                                                <td className={`px-6 py-4 text-right font-medium ${holding.changePercent >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                                    {holding.changePercent > 0 ? '+' : ''}{holding.changePercent.toFixed(2)}%
                                                </td>
                                            </>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* Footer / Loading State */}
                <div className={`p-4 border-t ${borderColor} flex flex-col items-center gap-2`}>
                    {searchQuery && (
                        <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                            {totalHoldings} results found
                        </div>
                    )}

                    {loading && (
                        <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                            Loading more holdings...
                        </div>
                    )}

                    {!hasMore && holdings.length > 0 && (
                        <div className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                            All holdings loaded
                        </div>
                    )}
                </div>
            </div>
        </div >
    );
};

export default InstitutionalOwnership;
